/**
 * Destroy the GAM slots of ads that have left the page.
 *
 * WHY. The deployed widget (`widget.sellwild.com/sellwild/sellwild-sellwild-next.js`)
 * registers `<sellwild-ad>` with no disconnect handler. Each insertion defines a
 * GPT slot and pushes it onto the page-global `swpbjs.gamSlots`; nothing ever
 * removes one. So a slot outlives its element: GPT keeps it (and its creative
 * iframe's bookkeeping), every auction refreshes it, and Prebid keeps bidding on
 * its ad unit. On an infinite feed, or across client-side navigations, that
 * grows with how far the reader has gone.
 *
 * The real fix is the widget's own `destroyAdSlotsWithin()` (PR
 * Antengo/sellwild-widget#4593), still unmerged. This does the same teardown
 * from our side, where we can deploy: everything here is a page global the
 * widget reads on each auction, so removing an entry is enough for the widget
 * to stop using it.
 *
 * HOW. A slot is reaped when its element was seen attached and is now gone
 * from the document. "Seen attached" matters: the widget can define a slot a
 * moment before its div is inserted, and a slot like that is not dead, it is
 * not born yet. Sweeps run after an `AdFramework` unmounts and before every
 * refresh (./ad-refresh-guard).
 *
 * REMOVE THIS once the widget destroys slots on disconnect.
 * `window.__SW_DISABLE_AD_SLOT_REAPER__ = true` before load turns it off
 * without a deploy.
 */

export interface ReapableSlot {
  getSlotElementId?: () => string;
}

interface SwPbjs {
  gamSlots?: ReapableSlot[];
  displaySlots?: ReapableSlot[];
  gamAdUnitCodes?: string[];
  displayAdUnitCodes?: string[];
  firstLoadedAdUnitCodes?: Record<string, unknown>;
  removeAdUnit?: (code: string) => void;
}

interface ReaperWindow {
  swpbjs?: SwPbjs;
  googletag?: {
    cmd?: unknown[];
    pubads?: () => { getSlots?: () => ReapableSlot[] };
    destroySlots?: (slots?: ReapableSlot[]) => boolean;
  };
  __SW_DISABLE_AD_SLOT_REAPER__?: boolean;
}

/** Element ids whose slot has been seen with its element in the document. */
const seenAttached = new Set<string>();

let reaped = 0;
let scheduled = false;
const listeners = new Set<(slots: ReapableSlot[]) => void>();

function slotId(slot: ReapableSlot): string | null {
  try {
    return slot.getSlotElementId?.() ?? null;
    // eslint-disable-next-line sellwild-code/catch-must-log -- an unreadable slot is not ours to judge; it is skipped, not an error
  } catch {
    return null;
  }
}

/** Whether a slot's element is in the document. Unknown counts as attached. */
export function isAdSlotAttached(slot: ReapableSlot): boolean {
  const id = slotId(slot);
  if (!id) return true;
  const el = document.getElementById(id);
  return !!el && el.isConnected;
}

/**
 * Run one sweep now. Returns how many slots it destroyed.
 *
 * Synchronous on the swpbjs side so an auction that starts right after cannot
 * find a dead slot; `destroySlots` itself goes through `googletag.cmd`, which
 * GPT drains in order.
 */
export function reapDetachedAdSlots(): number {
  if (typeof window === 'undefined') return 0;
  const w = window as unknown as ReaperWindow;
  if (w.__SW_DISABLE_AD_SLOT_REAPER__) return 0;

  const sw = w.swpbjs;
  const known: ReapableSlot[] = [
    ...(Array.isArray(sw?.gamSlots) ? sw.gamSlots : []),
    ...(Array.isArray(sw?.displaySlots) ? sw.displaySlots : []),
  ];
  // GPT's own list catches slots the widget defined but has not pushed yet.
  try {
    const fromGpt = w.googletag?.pubads?.().getSlots?.();
    if (Array.isArray(fromGpt)) known.push(...fromGpt);
    // eslint-disable-next-line sellwild-code/catch-must-log -- GPT not loaded yet; the widget arrays are enough
  } catch {
    /* GPT not ready */
  }

  const doomedIds = new Set<string>();
  const doomed: ReapableSlot[] = [];
  for (const slot of new Set(known)) {
    const id = slotId(slot);
    if (!id) continue;
    if (isAdSlotAttached(slot)) {
      seenAttached.add(id);
      continue;
    }
    if (!seenAttached.has(id)) continue;
    doomedIds.add(id);
    doomed.push(slot);
  }
  if (doomed.length === 0) return 0;

  if (sw) {
    const keep = (slot: ReapableSlot) => !doomedIds.has(slotId(slot) ?? '');
    if (Array.isArray(sw.gamSlots)) sw.gamSlots = sw.gamSlots.filter(keep);
    if (Array.isArray(sw.displaySlots)) sw.displaySlots = sw.displaySlots.filter(keep);
    if (Array.isArray(sw.gamAdUnitCodes)) sw.gamAdUnitCodes = sw.gamAdUnitCodes.filter((c) => !doomedIds.has(c));
    if (Array.isArray(sw.displayAdUnitCodes)) {
      sw.displayAdUnitCodes = sw.displayAdUnitCodes.filter((c) => !doomedIds.has(c));
    }
    if (sw.firstLoadedAdUnitCodes) doomedIds.forEach((id) => delete sw.firstLoadedAdUnitCodes![id]);
    if (typeof sw.removeAdUnit === 'function') {
      doomedIds.forEach((code) => {
        try {
          sw.removeAdUnit!(code);
          // eslint-disable-next-line sellwild-code/catch-must-log -- Prebid may never have known this unit; nothing to undo
        } catch {
          /* unknown unit */
        }
      });
    }
  }

  doomedIds.forEach((id) => seenAttached.delete(id));
  listeners.forEach((fn) => fn(doomed));
  reaped += doomed.length;

  if (w.googletag) {
    w.googletag.cmd = w.googletag.cmd || [];
    (w.googletag.cmd as (() => void)[]).push(() => {
      try {
        w.googletag?.destroySlots?.(doomed);
        // eslint-disable-next-line sellwild-code/catch-must-log -- already destroyed by someone else; the goal is met
      } catch {
        /* already gone */
      }
    });
  }

  return doomed.length;
}

/**
 * Sweep once the current commit has settled. Coalesces: a row unmounting three
 * ads schedules one sweep, not three.
 */
export function scheduleAdSlotReap(): void {
  if (typeof window === 'undefined' || scheduled) return;
  scheduled = true;
  setTimeout(() => {
    scheduled = false;
    reapDetachedAdSlots();
  }, 0);
}

/** Be told which slots a sweep destroyed, so other bookkeeping can drop them. */
export function onAdSlotsReaped(fn: (slots: ReapableSlot[]) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Slots destroyed so far in this document — for tests and diagnostics. */
export function reapedAdSlotCount(): number {
  return reaped;
}

/** Tests only. */
export function resetAdSlotReaper(): void {
  seenAttached.clear();
  reaped = 0;
  scheduled = false;
}
